"use client";

import { useCallback, useEffect, useId, useRef, type ReactNode } from "react";

// Tur kartındaki tarih çipleri: üzerine gelince (masaüstü), dokununca (mobil) ya da klavyeyle odaklanınca turun bütün
// kalkışlarını başlangıç–bitiş olarak listeleyen kutu. Kart bir bağlantı olduğu için tetikleyici kartın bağlantı
// katmanının üstündedir (z-20): çiplere tıklamak tura gitmez, kutuyu açar. Kutu HTML popover (üst katman): kartın
// overflow-hidden kenarında kırpılmaz; Esc ve dışarı tıklama tarayıcı tarafından kapatır. WCAG 1.4.13: fare kutunun
// içine geçebilir (kapanma gecikmeli), kutu odak/üzerine gelme sürdükçe açık kalır, Esc ile kapanır.

type Range = { visible: string; spoken: string };

const WIDTH = 288;
const GAP = 6;

export function DepartureDatesPopover({ label, title, ranges, children }: { label: string; title: string; ranges: Range[]; children: ReactNode }) {
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);
  // Dokunmatikte tarayıcı üzerine gelme + odak + tıklama olaylarını art arda gönderir: dokunuşu yalnızca tıklama yönetir
  const touchAt = useRef(0);
  const recentTouch = () => Date.now() - touchAt.current < 800;

  const isOpen = () => popover.current?.matches(":popover-open") ?? false;

  // Tetikleyicinin altına (yer yoksa üstüne) yerleşir; ekran kenarından taşmaz
  const place = useCallback(() => {
    const b = button.current?.getBoundingClientRect();
    const p = popover.current;
    if (!b || !p) return;
    const left = Math.min(Math.max(8, b.left), window.innerWidth - Math.min(WIDTH, window.innerWidth - 16) - 8);
    const below = b.bottom + GAP;
    const top = below + p.offsetHeight > window.innerHeight - 8 ? Math.max(8, b.top - GAP - p.offsetHeight) : below;
    p.style.left = `${left}px`;
    p.style.top = `${top}px`;
  }, []);

  const cancelClose = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const open = useCallback(() => {
    cancelClose();
    if (!popover.current || isOpen()) return;
    popover.current.showPopover();
    place();
  }, [place]);
  const close = useCallback(() => {
    cancelClose();
    if (isOpen()) popover.current?.hidePopover();
  }, []);
  const closeSoon = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(close, 180);
  };

  // aria-expanded popover'ın gerçek durumunu izler (Esc ve dışarı tıklamayla tarayıcı da kapatır)
  useEffect(() => {
    const p = popover.current;
    if (!p) return;
    const onToggle = (e: Event) => button.current?.setAttribute("aria-expanded", String((e as ToggleEvent).newState === "open"));
    // Kaydırmada kutu tetikleyiciyle birlikte yer değiştirir; tetikleyici ekrandan çıkınca kapanır
    const onScroll = () => {
      if (!isOpen()) return;
      const r = button.current?.getBoundingClientRect();
      if (!r || r.bottom < 0 || r.top > window.innerHeight) close();
      else place();
    };
    p.addEventListener("toggle", onToggle);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      p.removeEventListener("toggle", onToggle);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelClose();
    };
  }, [close, place]);

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label={label}
        aria-expanded="false"
        aria-controls={id}
        // Açıcı olarak tanımlı: tarayıcının "dışarı tıklayınca kapat"ı düğmeye dokunuşu dışarı saymaz (ikinci dokunuş
        // kapatabilsin). Yerleşik aç/kapa onClick'teki preventDefault ile devre dışı; durum bu bileşende yönetilir.
        popoverTarget={id}
        onPointerDown={(e) => {
          if (e.pointerType === "touch") touchAt.current = Date.now();
        }}
        onPointerEnter={(e) => {
          if (e.pointerType !== "touch") open();
        }}
        onPointerLeave={(e) => {
          if (e.pointerType !== "touch") closeSoon();
        }}
        onFocus={() => {
          if (!recentTouch()) open();
        }}
        onBlur={(e) => {
          if (!popover.current?.contains(e.relatedTarget as Node | null)) closeSoon();
        }}
        onClick={(e) => {
          // Dokunmatik: üzerine gelme yok, dokunuş açar/kapatır. Fare: üzerine gelmeyle zaten açık, tıklama açık tutar.
          e.preventDefault();
          if (recentTouch() && isOpen()) close();
          else open();
        }}
        className="relative z-20 -m-1 cursor-help rounded-md p-1 text-left focus-visible:ring-3 focus-visible:ring-focus focus-visible:outline-none"
      >
        {children}
      </button>
      <div
        ref={popover}
        id={id}
        popover="auto"
        role="dialog"
        aria-label={title}
        onMouseEnter={cancelClose}
        onMouseLeave={closeSoon}
        className="fixed m-0 max-w-[calc(100vw-1rem)] overflow-hidden rounded-lg border-0 bg-surface p-3 text-ink shadow-raised ring-1 ring-line [inset:auto]"
        style={{ width: WIDTH }}
      >
        <p className="px-1 pb-2 text-xs font-bold tracking-[0.08em] text-ink-muted uppercase">{title}</p>
        {/* Uzun liste kaydırılır: klavyeyle de kaydırılabilsin diye odaklanabilir (WCAG 2.1.1). "relative": gizli (sr-only)
            metinler listenin kaydırma alanında kalır; yoksa kutuya göre konumlanıp ikinci bir kaydırma çubuğu açıyorlardı. */}
        <ul tabIndex={ranges.length > 7 ? 0 : undefined} aria-label={title} className="relative grid max-h-64 gap-1 overflow-y-auto overscroll-contain rounded-md focus-visible:ring-3 focus-visible:ring-focus focus-visible:outline-none">
          {ranges.map((r, i) => (
            <li key={i} className="tabular rounded-md bg-surface-alt px-2.5 py-1.5 text-sm font-semibold">
              <span aria-hidden="true">{r.visible}</span>
              <span className="sr-only">{r.spoken}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
